import { useRef, useState, useEffect, useMemo, createRef, Fragment } from 'react';
import classNames from 'classnames';
import * as yup from 'yup';
import { Formik, Form, FieldArray, useFormikContext } from 'formik';
import { Popover, Transition } from '@headlessui/react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ShortUniqueId from 'short-unique-id';

import { useMainMachine } from '../../../utils/useMainMachine';
import { useCrazyAI } from '../../../ai/useCrazyAI';
import { Modal } from '../../../modal/Modal';
import { Button } from '../basic-ui/Button';
import { Input } from '../basic-ui/Input';
import { Tooltip } from '../basic-ui/Tooltip';
import { TextArea } from '../basic-ui/TextArea';
import { CloseButton } from '../basic-ui/CloseButton';
import { Checkbox } from '../basic-ui/Checkbox';
import { Select } from '../basic-ui/Select';
import { getNextVoiceColor, formatChecksForAI } from '../../../utils/utils';
import { appendCopy } from '../../../utils/string';
import { MODAL_ACTIONS } from '../../../modal/modalMachine';
import { ACTIONS } from '../../../state/actions';
import {
  BUTTON_TYPES,
  LUCKY,
  NEW,
  CHECK_NAME_LIMIT,
  VOICE_NAME_LIMIT,
  AI_PROMPTS,
  CHECK_DEFINITION_LIMIT,
} from '../../constants';

import ProfileIcon from '@crazyegginc/hatch/dist/images/icon-goal-filled.svg?react';
import VoiceIcon from '@crazyegginc/hatch/dist/images/icon-campaign-outline.svg?react';
import ChecksIcon from '@crazyegginc/hatch/dist/images/icon-checks.svg?react';
import InfoIcon from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg?react';
import PlusIcon from '@crazyegginc/hatch/dist/images/icon-plus.svg?react';
import MoveIcon from '@crazyegginc/hatch/dist/images/icon-move-dots.svg?react';
import MenuIcon from '@crazyegginc/hatch/dist/images/icon-menu-dots.svg?react';
import EditIcon from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg?react';
import DuplicateIcon from '@crazyegginc/hatch/dist/images/icon-pages-outline.svg?react';
import ShowIcon from '@crazyegginc/hatch/dist/images/icon-watched-outine.svg?react';
import HideIcon from '@crazyegginc/hatch/dist/images/icon-unwatched-outline.svg?react';
import DeleteIcon from '@crazyegginc/hatch/dist/images/icon-remove-filled.svg?react';
import WarningIcon from '@crazyegginc/hatch/dist/images/icon-warning-circle-filled.svg?react';
import BadgeTickIcon from '@crazyegginc/hatch/dist/images/icon-badge-tick.svg?react';
import BadgeCrossIcon from '@crazyegginc/hatch/dist/images/icon-badge-cross.svg?react';
import WarningTriangleIcon from '@crazyegginc/hatch/dist/images/icon-warning-filled.svg?react';
import TickIcon from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg?react';

const MAX_VOICES = 15;
const MAX_CHECKS = 10;

const uid = new ShortUniqueId({ length: 6 });

const voiceSchema = yup.object().shape({
  name: yup.string().required('Please provide a name.').max(VOICE_NAME_LIMIT, 'Maximum characters ${max}.'),
  sample: yup.string().max(1000, 'Maximum characters ${max}.'),
  definition: yup.string().required('Please provide the voice definition.').max(1000, 'Maximum characters ${max}.'),
  enabled: yup.boolean(),
});
const voiceSchemaWithUniqueName = (names) =>
  voiceSchema.concat(
    yup.object().shape({
      name: yup
        .string()
        .required('Please provide a name.')
        .max(VOICE_NAME_LIMIT, 'Maximum characters ${max}.')
        .test('unique', 'This name is taken, please choose another.', (n) => {
          if (!n) return true;
          return ![...names, LUCKY].includes(n); // you can't do "lucky" either
        }),
    }),
  );

const checkSchema = yup.object().shape({
  name: yup.string().required('Please provide a name.').max(CHECK_NAME_LIMIT, 'Maximum characters ${max}.'),
  note: yup.string().max(250, 'Maximum characters ${max}.'),
  condition: yup.string().oneOf(['pass', 'fail']),
  definition: yup
    .string()
    .required('Please provide the check definition.')
    .max(CHECK_DEFINITION_LIMIT, 'Maximum characters ${max}.'),
  example: yup.string().max(1000, 'Maximum characters ${max}.'),
  testContent: yup.string().max(2000, 'Maximum characters ${max}.'),
  enabled: yup.boolean(),
});
const checkSchemaWithUniqueName = (names) =>
  checkSchema.concat(
    yup.object().shape({
      name: yup
        .string()
        .required('Please provide a name.')
        .max(CHECK_NAME_LIMIT, 'Maximum characters ${max}.')
        .test('unique', 'This name is taken, please choose another.', (n) => {
          if (!n) return true;
          return !names.includes(n);
        }),
    }),
  );

const validationSchema = yup.object().shape({
  siteContext: yup.string().max(2000, 'Maximum characters ${max}'),
  audience: yup.string().max(2000, 'Maximum characters ${max}'),
  voices: yup.array().of(voiceSchema).max(MAX_VOICES),
  voiceTestContent: yup.string().max(2000, 'Maximum characters ${max}'),
  checks: yup.array().of(checkSchema).max(MAX_CHECKS),
});

export function BrandGuidelinesModal() {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [voiceEditorState, setVoiceEditorState] = useState({
    open: false,
    index: null,
    nextIndex: null,
    submitAttempt: null,
  });
  const [checkEditorState, setCheckEditorState] = useState({
    open: false,
    index: null,
    nextIndex: null,
    submitAttempt: null,
  });

  const { useSelector, machine } = useMainMachine();
  const brandGuidelines = useSelector(({ context }) => context.brandGuidelines);
  const site = useSelector(({ context }) => context.editor.pages.find((p) => p.id === context.activePageId)?.domain);
  const [initialValues] = useState(brandGuidelines);
  const [metadata, setMetadata] = useState({
    voices_edited: [],
    voices_organized: false,
    checks_edited: [],
    checks_enabled_disabled: [],
    audience_edited: false,
    site_context_edited: false,
  });
  const sectionRefs = useMemo(
    () => ({
      profile: createRef(),
      voice: createRef(),
      checks: createRef(),
    }),
    [],
  );

  const containerRef = useRef(null);
  const [activeSection, setActiveSection] = useState('profile');

  const scrollToSection = (key) => {
    const section = sectionRefs[key]?.current;
    if (section && containerRef.current) {
      containerRef.current.scrollTo({
        top: section.offsetTop - 35,
        behavior: 'smooth',
      });
    }
  };

  const closeModal = () => {
    machine.send({
      type: MODAL_ACTIONS.CLOSE,
    });
  };

  const onCancel = (dirty) => {
    if (dirty) {
      setShowConfirmDialog(true);
      return;
    }
    closeModal();
  };

  const debounceRef = useRef(null);
  const visibilityMapRef = useRef({});

  useEffect(() => {
    const container = containerRef.current;

    const checkMostVisible = () => {
      const visibleEntries = Object.entries(visibilityMapRef.current)
        .filter(([, ratio]) => ratio > 0.1)
        .sort(([, a], [, b]) => b - a);

      if (visibleEntries.length === 0) return;

      const topRatio = visibleEntries[0][1];
      const topCandidates = visibleEntries.filter(([, ratio]) => ratio === topRatio);

      let nextActive = topCandidates[0][0];

      const scrolledToBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= 1;

      if (topCandidates.length > 1 && scrolledToBottom) {
        nextActive = topCandidates[topCandidates.length - 1][0];
      }

      if (nextActive !== activeSection) {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
          setActiveSection(nextActive);
        }, 50);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        let hasUpdate = false;

        entries.forEach((entry) => {
          const id = entry.target.id;
          const newRatio = entry.isIntersecting ? entry.intersectionRatio : 0;
          const prevRatio = visibilityMapRef.current[id] || 0;

          if (newRatio !== prevRatio) {
            visibilityMapRef.current[id] = newRatio;
            hasUpdate = true;
          }
        });

        if (hasUpdate) {
          checkMostVisible();
        }
      },
      {
        root: container,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      },
    );

    // Observe each section
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    // Also check visibility on every scroll
    container.addEventListener('scroll', checkMostVisible);

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', checkMostVisible);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [sectionRefs, activeSection]);

  return (
    <Modal
      dialogClassName="!w-[700px] !p-2.5 !max-h-[min(70%,_600px)] !overflow-hidden"
      dismissable={false}
      closeButtonClass="!right-7"
      disableOutsideClick={true}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (voiceEditorState.open) {
            setVoiceEditorState((x) => ({ ...x, submitAttempt: true }));
            return;
          }
          if (checkEditorState.open) {
            setCheckEditorState((x) => ({ ...x, submitAttempt: true }));
            return;
          }
          machine.send({
            type: ACTIONS.UPDATE_BRAND_GUIDELINES,
            domain: site,
            brandGuidelines: { ...values, updatedAt: Math.floor(Date.now() / 1000) },
            metadata,
          });
          closeModal();
        }}
      >
        {({ dirty }) => (
          <>
            <CloseButton
              label="close modal"
              onClick={() => onCancel(dirty || voiceEditorState.open || checkEditorState.open)}
              buttonClass="!right-7"
            />
            {showConfirmDialog ? (
              <>
                <div className="absolute w-full h-full bg-gray-900/65 left-0 top-0 z-[10000000]" />
                <div
                  className="absolute left-1/2 top-[200px] -translate-x-1/2 w-[460px] bg-gray-600 rounded-lg shadow-md px-10 pb-[30px] pt-10 z-[10000000]"
                  data-testid="DiscardChangesModal"
                >
                  <WarningTriangleIcon className="w-9 h-9 fill-current text-[#FFB202]" />
                  <div className="mt-2 text-2xl font-semibold">Discard changes?</div>
                  <p className="text-gray-200">
                    If you exit now, all unsaved changes to your Brand Guidelines will be lost. This can’t be undone.
                  </p>
                  <div className="flex items-center w-full justify-end space-x-2 mt-10">
                    <Button variant={BUTTON_TYPES.CANCEL} onClick={closeModal} size="lg">
                      Discard and exit
                    </Button>
                    <Button onClick={() => setShowConfirmDialog(false)} size="lg">
                      Keep editing
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
            <Form className="contents">
              <div className="flex max-h-[calc(100%_-_60px)] space-x-2.5 overflow-y-auto styled-scrollbar flex-auto">
                <div className="w-[60px] shrink-0">
                  <NavItem
                    Icon={ProfileIcon}
                    text="Profile"
                    active={activeSection === 'profile'}
                    onClick={() => scrollToSection('profile')}
                  />
                  <NavItem
                    Icon={VoiceIcon}
                    text="Voice"
                    active={activeSection === 'voice'}
                    onClick={() => scrollToSection('voice')}
                  />
                  <NavItem
                    Icon={ChecksIcon}
                    text="Checks"
                    active={activeSection === 'checks'}
                    onClick={() => scrollToSection('checks')}
                  />
                </div>
                <div
                  className="rounded-md bg-gray-800 grow overflow-y-auto styled-scrollbar pl-[26px] pr-8 py-4 text-gray-200 text-sm"
                  ref={containerRef}
                >
                  <div className="text-white text-xl font-semibold mb-3.75 leading-none">Brand Guidelines</div>
                  <div className="mb-10">
                    Set brand guidelines for {site} to ensure your content always reflects your brand.
                  </div>
                  <section id="profile" ref={sectionRefs.profile}>
                    <ProfileSection setMetadata={setMetadata} />
                  </section>
                  <section id="voice" ref={sectionRefs.voice}>
                    <VoiceSection
                      containerRef={containerRef}
                      voiceEditorState={voiceEditorState}
                      setVoiceEditorState={setVoiceEditorState}
                      setMetadata={setMetadata}
                    />
                  </section>
                  <section id="checks" ref={sectionRefs.checks}>
                    <ChecksSection
                      containerRef={containerRef}
                      checkEditorState={checkEditorState}
                      setCheckEditorState={setCheckEditorState}
                      setMetadata={setMetadata}
                    />
                  </section>
                </div>
              </div>
              <div className="mt-2.5 ml-[70px] flex items-center space-x-2 justify-center">
                <Button variant={BUTTON_TYPES.CANCEL} onClick={() => onCancel(dirty)}>
                  Cancel
                </Button>
                <Button type="submit">Save</Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </Modal>
  );
}

function NavItem({ Icon, text, active, onClick }) {
  return (
    <button
      className={classNames('w-[60px] h-[60px] rounded-md flex flex-col items-center justify-center text-2xs', {
        'text-white bg-gray-600': active,
        'text-gray-200 hover:text-white': !active,
      })}
      type="button"
      onClick={onClick}
    >
      <Icon className="w-6 h-6 fill-current" />
      {text}
    </button>
  );
}

function ProfileSection({ setMetadata }) {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext();

  return (
    <>
      <div className="flex items-center space-x-2.5 text-white text-base font-semibold mb-3.75">
        <ProfileIcon className="w-4 h-4 fill-current" />
        <span>Profile</span>
      </div>
      <div className="text-gray-200 mb-6">
        Provide some background to help us generate text that matches your brand.
      </div>
      <div className="mb-6">
        <TextArea
          label="What is the purpose of this site?"
          labelClassName="!mb-2.5"
          placeholder="For example, “To provide information about our course and encourage visitors to sign up”."
          name="profile.context"
          value={values.profile.context}
          onBlur={handleBlur}
          onChange={(e) => {
            handleChange(e);
            setMetadata((x) => ({ ...x, site_context_edited: true }));
          }}
          error={errors?.profile?.context && touched?.profile?.context ? errors?.profile.context : null}
        />
      </div>
      <TextArea
        label="Who is the intended audience?"
        labelClassName="!mb-2.5"
        placeholder="For example, “Shoppers looking for quality products at competitive prices”."
        name="profile.audience"
        value={values.profile.audience}
        onBlur={handleBlur}
        onChange={(e) => {
          handleChange(e);
          setMetadata((x) => ({ ...x, audience_edited: true }));
        }}
        error={errors?.profile?.audience && touched?.profile?.audience ? errors?.profile.audience : null}
      />
      <div className="bg-gray-500 h-px my-10" />
    </>
  );
}

function VoiceSection({ containerRef, voiceEditorState, setVoiceEditorState, setMetadata }) {
  const { values } = useFormikContext();
  const scrollRef = useRef();

  function addVoiceClicked() {
    if (!voiceEditorState.open || voiceEditorState.index === NEW) {
      setVoiceEditorState({ open: true, index: NEW, nextIndex: null, submitAttempt: null });
    } else if (voiceEditorState.open && voiceEditorState.index !== NEW) {
      setVoiceEditorState((x) => ({ ...x, nextIndex: NEW, submitAttempt: null }));
    }
  }

  function editVoiceClicked(index) {
    if (!voiceEditorState.open || voiceEditorState.index === index) {
      setVoiceEditorState({ open: true, index, nextIndex: null, submitAttempt: null });
    } else if (voiceEditorState.open && voiceEditorState.index !== index) {
      setVoiceEditorState((x) => ({ ...x, nextIndex: index, submitAttempt: null }));
    }
  }

  function closeEditor() {
    if (voiceEditorState.nextIndex == null) {
      setVoiceEditorState({ open: false, index: null, nextIndex: null, submitAttempt: null });
      containerRef.current.scrollTo({
        top: scrollRef.current.offsetTop - 20,
        behavior: 'smooth',
      });
    } else {
      setVoiceEditorState({ open: true, index: voiceEditorState.nextIndex, nextActive: null, submitAttempt: null });
    }
  }

  return (
    // An explicit context keeps one drag-drop manager for the page. With the default global context,
    // StrictMode's effect replay clears react-dnd's singleton, so the next re-render creates a new
    // backend and tears down the one handling an in-progress drag.
    <DndProvider backend={HTML5Backend} context={window}>
      <FieldArray name={'voices'}>
        {(actions) => (
          <>
            <div className="flex items-center justify-between w-full mb-4" ref={scrollRef}>
              <div className="flex items-center space-x-2.5 text-white text-base font-semibold">
                <VoiceIcon className="w-4 h-4 fill-current" />
                <span>Voice</span>
                <Tooltip
                  arrowSkiddingPercent={15}
                  placement="bottom"
                  tooltipContent={
                    <div className="w-56 text-left">
                      Change the writing style of your content via the ‘voice’ dropdown. Find it next to the ‘Generate’
                      button at the top of your page.
                    </div>
                  }
                  containerStyle={{ zIndex: 99999 }}
                >
                  <InfoIcon className="w-4 h-4 fill-current cursor-pointer" />
                </Tooltip>
              </div>
              <div className="flex items-center">
                {values.voices.length >= MAX_VOICES ? (
                  <div className="mr-2.5 text-gray-300 text-xs">Max {MAX_VOICES} voices reached</div>
                ) : null}
                <Button
                  variant={BUTTON_TYPES.SECONDARY}
                  onClick={addVoiceClicked}
                  disabled={values.voices.length >= MAX_VOICES}
                >
                  <PlusIcon className="w-2.5 h-2.5 fill-current mr-2.5" />
                  Add voice
                </Button>
              </div>
            </div>
            <div className="mb-2.5">
              What is your brand personality? Use different voices to tailor your content to any situation.
            </div>
            {voiceEditorState.open && voiceEditorState.index === NEW ? (
              <VoiceEditor
                close={closeEditor}
                unshift={actions.unshift}
                voiceEditorState={voiceEditorState}
                containerRef={containerRef}
                setMetadata={setMetadata}
              />
            ) : null}
            <Voices
              actions={actions}
              editVoiceClicked={editVoiceClicked}
              voiceEditorState={voiceEditorState}
              closeEditor={closeEditor}
              containerRef={containerRef}
              setMetadata={setMetadata}
            />
          </>
        )}
      </FieldArray>
      <div className="bg-gray-500 h-px my-10" />
    </DndProvider>
  );
}

function VoiceEditor({ close, unshift, voiceEditorState, containerRef, replace, index, setMetadata }) {
  const mainFormik = useFormikContext();
  const { useSelector } = useMainMachine();
  const userEmail = useSelector(({ context }) => context.userEmail);

  const voice = index != null ? mainFormik.values.voices[index] : null;
  const [initialValues] = useState(
    voice
      ? {
          ...voice,
          updatedBy: userEmail,
        }
      : {
          name: '',
          updatedBy: userEmail,
          createdBy: userEmail,
          color: getNextVoiceColor(mainFormik.values.voices.map((v) => v.color)),
          definition: '',
          sample: '',
          enabled: true,
        },
  );

  let names = mainFormik.values.voices.map((v) => v.name);
  if (index != null) {
    names = [...names.slice(0, index), ...names.slice(index + 1)];
  }
  const scrollRef = useRef();
  const scrollRef2 = useRef();

  useEffect(() => {
    if (voiceEditorState.nextIndex != null || voiceEditorState.submitAttempt != null) {
      containerRef.current.scrollTo({
        top: scrollRef.current.offsetTop - containerRef.current.clientHeight - 20,
        behavior: 'smooth',
      });
    }
  }, [voiceEditorState.nextIndex, voiceEditorState.submitAttempt, containerRef]);

  useEffect(() => {
    containerRef.current.scrollTo({
      top: scrollRef2.current.offsetTop - 20,
      behavior: 'smooth',
    });
  }, [containerRef]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        if (voice) {
          replace(index, values);
          setMetadata((x) => ({
            ...x,
            voices_edited: [...x.voices_edited, { name: values.name, action: 'edited' }],
          }));
        } else {
          setMetadata((x) => ({
            ...x,
            voices_edited: [...x.voices_edited, { name: values.name, action: 'added' }],
          }));
          unshift(values);
        }
        close();
      }}
      validationSchema={voiceSchemaWithUniqueName(names)}
    >
      {({ values, errors, touched, handleChange, handleBlur, submitForm }) => (
        <>
          <div
            className="w-full rounded-md border border-blue-500/30 relative p-3.75 mb-3.75 bg-gray-700"
            ref={scrollRef2}
            data-testid="VoiceEditor"
          >
            <CloseButton onClick={close} />
            <div className="mb-3.75 text-white font-semibold leading-none">{voice ? 'Edit' : 'Create'} voice</div>
            <div className="bg-gray-500 h-px mb-5 -mx-3.75" />
            <Input
              label="Name your voice"
              labelClassName="!mb-2.5"
              placeholder="e.g. “Brand voice”"
              size="lg"
              wrapperClassName="mb-5"
              charLimit={VOICE_NAME_LIMIT}
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && errors.name ? errors.name : null}
            />
            <TextArea
              label={
                <div>
                  Add sample text <span className="text-gray-200 font-normal">(optional)</span>
                </div>
              }
              labelClassName="!mb-2.5"
              placeholder="Paste some sample text from your site, emails or marketing materials to help our AI learn your writing style"
              wrapperClassName="mb-5"
              name="sample"
              value={values.sample}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.sample && errors.sample ? errors.sample : null}
            />
            <TextArea
              label="How should this voice sound?"
              labelClassName="!mb-2.5"
              placeholder="Describe your brand voice, e.g. “Our voice is positive, casual and warm”"
              wrapperClassName="mb-5"
              name="definition"
              value={values.definition}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.definition && errors.definition ? errors.definition : null}
            />
            <div className="text-white font-semibold mb-2.5">
              Test your voice <span className="text-gray-200 font-normal">(optional)</span>
            </div>
            <TextArea
              label="Enter your own text or use the example below to see if your voice sounds as intended"
              labelClassName="!mb-3 !font-normal !text-gray-200"
              placeholder="Describe your brand voice, e.g. “Our voice is positive, casual and warm”"
              wrapperClassName="mb-2.5"
              name="voiceTestContent"
              value={mainFormik.values.voiceTestContent}
              onChange={mainFormik.handleChange}
              onBlur={mainFormik.handleBlur}
              error={
                mainFormik.touched.voiceTestContent && mainFormik.errors.voiceTestContent
                  ? mainFormik.errors.voiceTestContent
                  : null
              }
            />
            <TestVoice
              voiceDescription={values.definition}
              voiceTestContent={mainFormik.values.voiceTestContent}
              voiceSample={values.sample}
            />
            {voiceEditorState.nextIndex != null || voiceEditorState.submitAttempt != null ? (
              <div className="flex items-center mb-2.5 text-white">
                <WarningIcon className="w-4 h-4 fill-current text-[#FF993B] mr-2.5" />
                Do you want to add this voice before continuing?
              </div>
            ) : null}
            <Button className="w-full" onClick={() => submitForm()}>
              {voice ? 'Update' : 'Add'} voice
            </Button>
          </div>
          <div ref={scrollRef} />
        </>
      )}
    </Formik>
  );
}

function TestVoice({ voiceDescription, voiceTestContent, voiceSample }) {
  const [clicked, setClicked] = useState(false);
  const [voice, setVoice] = useState(voiceDescription);
  const [text, setText] = useState(voiceTestContent);
  const [sample, setSample] = useState(voiceSample);

  const params = useMemo(() => {
    return { voice, text, sample };
  }, [voice, text, sample]);

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
    }),
    [],
  );

  const {
    data,
    error: aiError,
    refetch,
  } = useCrazyAI({
    type: AI_PROMPTS.VOICE_TEST,
    params,
    context,
    enabled: clicked,
  });

  function renderResult() {
    const loading = !data?.ready;
    const error = aiError || (data?.ready && !data.text);

    if (error) {
      return (
        <div className="w-full h-[63px] border border-gray-400 rounded-md p-3 mb-2.5 text-gray-200">
          There was an error while testing your example. Please try again.
        </div>
      );
    } else {
      if (loading) {
        return (
          <div
            className={classNames(
              'w-full h-[70px] mt-2.5 rounded-md bg-gray-500/40 animate-skeleton',
              'bg-gradient-to-r from-gray-500/40 via-[20%] via-gray-500/10 to-[40%] to-gray-500/40 bg-[length:1000px]',
            )}
          />
        );
      }
      return (
        <div
          style={{ '--selectedvoice': '122 76 252' }}
          className="w-full mt-2.5 p-2.5 rounded-md border bg-selectedvoice/10 text-[#B89EFF] border-selectedvoice/50"
        >
          {data.text}
        </div>
      );
    }
  }

  return (
    <div className="mb-6">
      <div className="w-full flex justify-end">
        <Button
          disabled={!voiceDescription || !voiceTestContent}
          variant={BUTTON_TYPES.SECONDARY}
          onClick={() => {
            let needsRefetch = true;
            if (voice !== voiceDescription) {
              setVoice(voiceDescription);
              needsRefetch = false;
            }
            if (text !== voiceTestContent) {
              setText(voiceTestContent);
              needsRefetch = false;
            }
            if (sample !== voiceSample) {
              setSample(voiceSample);
              needsRefetch = false;
            }
            if (!clicked) {
              setClicked(true);
              needsRefetch = false;
            }
            if (needsRefetch) {
              refetch();
            }
          }}
        >
          Test voice
        </Button>
      </div>
      {clicked ? (
        <div className="mt-5 text-sm">
          <div className="text-white">Text with voice applied:</div>
          {renderResult()}
        </div>
      ) : null}
    </div>
  );
}

function Voices({ actions, editVoiceClicked, voiceEditorState, closeEditor, containerRef, setMetadata }) {
  const [showAll, setShowAll] = useState(false);
  const { values } = useFormikContext();
  const voices = showAll ? values.voices : values.voices.slice(0, 6);

  return (
    <>
      {voices.map((voice, index) => (
        <Fragment key={voice.name}>
          {voiceEditorState.open && voiceEditorState.index === index ? (
            <VoiceEditor
              close={closeEditor}
              voiceEditorState={voiceEditorState}
              containerRef={containerRef}
              replace={actions.replace}
              index={index}
              setMetadata={setMetadata}
            />
          ) : (
            <VoiceItem index={index} actions={actions} editVoiceClicked={editVoiceClicked} setMetadata={setMetadata} />
          )}
        </Fragment>
      ))}
      {values.voices.length > 6 && (
        <>
          <div className="w-full flex justify-center">
            <button
              type="button"
              className="font-semibold text-white"
              onClick={() => {
                setShowAll(!showAll);
              }}
            >
              {showAll ? 'Show less' : 'Show more'}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function VoiceItem({ actions, index, editVoiceClicked, setMetadata }) {
  const ref = useRef(null);
  const dragRef = useRef(null);
  const { values } = useFormikContext();
  const { useSelector } = useMainMachine();

  const userEmail = useSelector(({ context }) => context.userEmail);
  const voice = values.voices[index];

  const canDrag = voice.enabled;
  const canEdit = voice.enabled && voice.id == null;
  const canDuplicate = voice.enabled && values.voices.length < MAX_VOICES;
  const canDelete = voice.id == null;

  const [, drop] = useDrop({
    accept: 'voiceItem',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    canDrop: () => true,
    hover(item, monitor) {
      if (!ref.current || !monitor.canDrop()) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      actions.move(dragIndex, hoverIndex);
      setMetadata((x) => ({ ...x, voices_organized: true }));
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'voiceItem',
    item: () => {
      return { id: voice.name, index };
    },
    canDrag: () => canDrag,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(ref));
  drag(dragRef);

  return (
    <div
      className={classNames('relative flex items-start mb-2.5 ', { 'opacity-0': isDragging })}
      ref={ref}
      data-testid="voice-row"
      style={{ '--selectedvoice': voice.color }}
    >
      <div className="flex h-10 items-center w-full">
        <div
          className={classNames('w-4 h-4 shrink-0 grow-0', {
            'cursor-[grab]': canDrag,
          })}
          ref={dragRef}
          data-testid="DragHandle"
        >
          {canDrag ? <MoveIcon className="h-4 w-4 fill-current" /> : null}
        </div>
        <div className="ml-2.5 h-10 pl-3.75 pr-20 border rounded-md border-gray-400 bg-gray-700 flex grow max-w-[calc(100%_-_26px)] items-center relative">
          <span
            className={classNames('shrink-0', {
              'text-selectedvoice': voice.enabled,
              'text-white/30 line-through': !voice.enabled,
            })}
          >
            {voice.name}
          </span>
          {voice.updatedBy ? (
            <span
              className={classNames('truncate', {
                'text-gray-300': voice.enabled,
                'text-white/30 line-through': !voice.enabled,
              })}
            >
              &nbsp;&nbsp;by {voice.updatedBy}
            </span>
          ) : null}
          <Popover className="absolute right-3">
            <Popover.Button className="p-1 text-gray-200 hover:text-white">
              <MenuIcon className="w-3 h-3 fill-current" />
            </Popover.Button>
            <Transition
              appear={true}
              as={Fragment}
              leave="transition-opacity ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              enter="transition-opacity ease-in duration-100"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <Popover.Panel className="absolute z-10 right-0 top-full bg-gray-500 rounded-md p-1.25 shadow-md flex flex-col w-[120px]">
                {({ close }) => (
                  <div className="text-gray-200 font-semibold">
                    {canEdit ? (
                      <MenuItem
                        title="Edit"
                        Icon={EditIcon}
                        onClick={() => {
                          editVoiceClicked(index);
                          close();
                        }}
                      />
                    ) : null}
                    {canDuplicate ? (
                      <MenuItem
                        title="Duplicate"
                        Icon={DuplicateIcon}
                        onClick={() => {
                          const newName = appendCopy(
                            voice.name,
                            values.voices.map((v) => v.name),
                            VOICE_NAME_LIMIT,
                          );
                          setMetadata((x) => ({
                            ...x,
                            voices_edited: [...x.voices_edited, { name: newName, action: 'added' }],
                          }));
                          actions.unshift({
                            ...voice,
                            id: undefined,
                            name: newName,
                            color: getNextVoiceColor(values.voices.map((v) => v.color)),
                            createdBy: userEmail,
                            updatedBy: userEmail,
                          });
                          editVoiceClicked(0);
                          close();
                        }}
                      />
                    ) : null}
                    <MenuItem
                      title={voice.enabled ? 'Hide' : 'Show'}
                      Icon={voice.enabled ? HideIcon : ShowIcon}
                      onClick={() => {
                        actions.replace(index, { ...voice, enabled: !voice.enabled });
                        if (voice.enabled) {
                          actions.move(index, values.voices.length - 1);
                        } else {
                          actions.move(index, 0);
                        }
                        setMetadata((x) => ({ ...x, voices_organized: true }));
                        close();
                      }}
                    />
                    {canDelete ? (
                      <MenuItem
                        title="Delete"
                        Icon={DeleteIcon}
                        onClick={() => {
                          actions.remove(index);
                          setMetadata((x) => ({
                            ...x,
                            voices_edited: [...x.voices_edited, { name: voice.name, action: 'deleted' }],
                          }));
                          close();
                        }}
                      />
                    ) : null}
                  </div>
                )}
              </Popover.Panel>
            </Transition>
          </Popover>
        </div>
      </div>
    </div>
  );
}

export function MenuItem({ Icon, title, onClick }) {
  return (
    <button
      type="button"
      className={classNames(
        'flex items-center pl-2.5 text-white h-[25px] hover:bg-blue-500 rounded w-full text-xs font-normal',
      )}
      onClick={onClick}
    >
      <Icon className="fill-current w-3 h-3 mr-2" />
      {title}
    </button>
  );
}

function ChecksSection({ containerRef, checkEditorState, setCheckEditorState, setMetadata }) {
  const { values } = useFormikContext();
  const scrollRef = useRef();

  function addCheckClicked() {
    if (!checkEditorState.open || checkEditorState.index === NEW) {
      setCheckEditorState({ open: true, index: NEW, nextIndex: null, submitAttempt: null });
    } else if (checkEditorState.open && checkEditorState.index !== NEW) {
      setCheckEditorState((x) => ({ ...x, nextIndex: NEW, submitAttempt: null }));
    }
  }

  function editCheckClicked(index) {
    if (!checkEditorState.open || checkEditorState.index === index) {
      setCheckEditorState({ open: true, index, nextIndex: null, submitAttempt: null });
    } else if (checkEditorState.open && checkEditorState.index !== index) {
      setCheckEditorState((x) => ({ ...x, nextIndex: index, submitAttempt: null }));
    }
  }

  function closeEditor() {
    if (checkEditorState.nextIndex == null) {
      setCheckEditorState({ open: false, index: null, nextIndex: null, submitAttempt: null });
      containerRef.current.scrollTo({
        top: scrollRef.current.offsetTop - 20,
        behavior: 'smooth',
      });
    } else {
      setCheckEditorState({ open: true, index: checkEditorState.nextIndex, nextActive: null, submitAttempt: null });
    }
  }

  return (
    <FieldArray name={'checks'}>
      {(actions) => (
        <>
          <div className="flex items-center justify-between w-full mb-3.75" ref={scrollRef} data-testid="checkModal">
            <div className="flex items-center space-x-2.5 text-white text-base font-semibold">
              <ChecksIcon className="w-4 h-4 fill-current" />
              <span>Checks</span>
            </div>
            <div className="flex items-center">
              {values.checks.length >= MAX_CHECKS ? (
                <div className="mr-2.5 text-gray-300 text-xs">Max {MAX_CHECKS} checks reached</div>
              ) : null}
              <Button
                variant={BUTTON_TYPES.SECONDARY}
                onClick={addCheckClicked}
                disabled={values.checks.length >= MAX_CHECKS}
              >
                <PlusIcon className="w-2.5 h-2.5 fill-current mr-2.5" />
                Add custom check
              </Button>
            </div>
          </div>
          <div className="mb-2.5">
            Select and customize smart checks to ensure your content meets your brand guidelines.
          </div>
          {checkEditorState.open && checkEditorState.index === NEW ? (
            <CheckEditor
              close={closeEditor}
              unshift={actions.unshift}
              checkEditorState={checkEditorState}
              containerRef={containerRef}
              setMetadata={setMetadata}
            />
          ) : null}
          <Checks
            actions={actions}
            editCheckClicked={editCheckClicked}
            checkEditorState={checkEditorState}
            closeEditor={closeEditor}
            containerRef={containerRef}
            setMetadata={setMetadata}
          />
          <div className="text-2xs mt-5 text-gray-300">
            Checks will automatically be applied whenever content is generated, manually edited or a new page is
            uploaded to the editor. Checks define what content is ‘brand compliant’, and cannot be applied to images.
          </div>
        </>
      )}
    </FieldArray>
  );
}

function CheckEditor({ close, unshift, checkEditorState, containerRef, replace, index, setMetadata }) {
  const mainFormik = useFormikContext();
  const { useSelector } = useMainMachine();
  const userEmail = useSelector(({ context }) => context.userEmail);

  const check = index != null ? mainFormik.values.checks[index] : null;
  const [initialValues] = useState(
    check
      ? {
          ...check,
          updatedBy: userEmail,
        }
      : {
          id: uid.rnd(),
          name: '',
          updatedBy: userEmail,
          createdBy: userEmail,
          note: '',
          condition: 'fail',
          definition: '',
          example: '',
          testContent: '',
          enabled: true,
          default: false,
        },
  );

  let names = mainFormik.values.checks.map((v) => v.name);
  if (index != null) {
    names = [...names.slice(0, index), ...names.slice(index + 1)];
  }
  const scrollRef = useRef();
  const scrollRef2 = useRef();

  useEffect(() => {
    if (checkEditorState.nextIndex != null || checkEditorState.submitAttempt != null) {
      containerRef.current.scrollTo({
        top: scrollRef.current.offsetTop - containerRef.current.clientHeight - 20,
        behavior: 'smooth',
      });
    }
  }, [checkEditorState.nextIndex, checkEditorState.submitAttempt, containerRef]);

  useEffect(() => {
    containerRef.current.scrollTo({
      top: scrollRef2.current.offsetTop - 20,
      behavior: 'smooth',
    });
  }, [containerRef]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        if (check) {
          setMetadata((x) => ({ ...x, checks_edited: [...x.checks_edited, { name: values.name, action: 'edited' }] }));
          replace(index, values);
        } else {
          setMetadata((x) => ({ ...x, checks_edited: [...x.checks_edited, { name: values.name, action: 'added' }] }));
          unshift(values);
        }
        close();
      }}
      validationSchema={checkSchemaWithUniqueName(names)}
    >
      {({ values, errors, touched, handleChange, handleBlur, submitForm, setFieldValue }) => (
        <>
          <div
            className="w-full rounded-md border border-blue-500/30 relative p-3.75 mb-3.75 bg-gray-700"
            ref={scrollRef2}
          >
            <CloseButton onClick={close} />
            <div className="mb-3.75 text-white font-semibold leading-none">{check ? 'Edit' : 'Create'} check</div>
            <div className="bg-gray-500 h-px mb-5 -mx-3.75" />
            <Input
              label="Name your check"
              labelClassName="!mb-2.5"
              placeholder="e.g. “Grammar”"
              size="lg"
              wrapperClassName="mb-5"
              charLimit={CHECK_NAME_LIMIT}
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && errors.name ? errors.name : null}
            />
            <Input
              label={
                <div>
                  What is this check for?{' '}
                  <span className="text-gray-200 font-normal">
                    (optional - add a short note to help you identify this check)
                  </span>
                </div>
              }
              labelClassName="!mb-2.5"
              placeholder="e.g. “Correct any spelling or grammatical errors”"
              size="lg"
              wrapperClassName="mb-5"
              name="note"
              value={values.note}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.note && errors.note ? errors.note : null}
            />
            <div className="flex items-center mb-2.5">
              Content will
              <div className="w-[80px] mx-1.25">
                <Select
                  aria-label="voice selector"
                  options={[{ value: 'fail' }, { value: 'pass' }]}
                  value={values.condition}
                  onChange={(value) => setFieldValue('condition', value)}
                  renderInPortal={true}
                  popperClassName="!z-[100000]"
                />
              </div>
              this check if:
            </div>
            <TextArea
              placeholder={`e.g. “There are ${values.condition === 'pass' ? 'no ' : ''}spelling or grammatical errors”`}
              wrapperClassName="mb-2.5"
              rows={4}
              name="definition"
              value={values.definition}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.definition && errors.definition ? errors.definition : null}
            />
            <ImproveCheckDefinition />
            <TextArea
              label={
                <div>
                  {values.condition === 'fail' ? 'Fail' : 'Pass'} example{' '}
                  <span className="text-gray-200 font-normal">(optional)</span>
                </div>
              }
              labelClassName="!mb-2.5"
              placeholder={`e.g. ${values.condition === 'fail' ? '“Their going too the store”' : '“They are going to the store”'}`}
              wrapperClassName="mb-5"
              name="example"
              value={values.example}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.example && errors.example ? errors.example : null}
            />
            <TextArea
              label={
                <div>
                  Test your check <span className="text-gray-200 font-normal">(optional)</span>
                </div>
              }
              labelClassName="!mb-2.5"
              placeholder="Enter text to test if your check works how you intend"
              wrapperClassName="mb-2.5"
              name="testContent"
              value={values.testContent}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.testContent && errors.testContent ? errors.testContent : null}
            />
            <TestCheck
              checkCondition={values.condition}
              checkDefinition={values.definition}
              checkExample={values.example}
              checkTestContent={values.testContent}
            />
            {checkEditorState.nextIndex != null || checkEditorState.submitAttempt != null ? (
              <div className="flex items-center mb-2.5 text-white">
                <WarningIcon className="w-4 h-4 fill-current text-[#FF993B] mr-2.5" />
                Do you want to add this check before continuing?
              </div>
            ) : null}
            <Button className="w-full" onClick={() => submitForm()}>
              {check ? 'Update' : 'Add'} check
            </Button>
          </div>
          <div ref={scrollRef} />
        </>
      )}
    </Formik>
  );
}

function ImproveCheckDefinition() {
  const { values, setFieldValue } = useFormikContext();
  const [clicked, setClicked] = useState(false);
  const [condition, setCondition] = useState(values.condition);
  const [definition, setDefinition] = useState(values.definition);

  const params = useMemo(() => {
    return {
      user_check_definition: formatChecksForAI({
        condition,
        definition,
      }).definition,
      char_limit: CHECK_DEFINITION_LIMIT - 100,
    };
  }, [condition, definition]);

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
    }),
    [],
  );

  const {
    data,
    error: aiError,
    refetch,
  } = useCrazyAI({
    type: AI_PROMPTS.IMPROVE_CHECK,
    params,
    context,
    enabled: clicked,
  });

  const result = useMemo(() => {
    if (!data?.ready) return null;
    let result;
    try {
      result = JSON.parse(data.text);
    } catch {
      return null;
    }
    return result;
  }, [data]);

  function renderResult() {
    const loading = !data?.ready;
    const noImprovement = result?.status === 'no_improvement';
    const cannotImprove = result?.status === 'cannot_improve';
    const improvedPrompt = result?.refined_prompt?.replace(/^Content will (pass|fail) this check if /, '');
    const improved = result?.status === 'improved';
    const error = aiError || (data?.ready && result == null);

    if (error) {
      return (
        <div className="w-full h-[63px] border border-gray-400 rounded-md p-3 mb-2.5 text-gray-200">
          There was an error while improving your check. Please try again.
        </div>
      );
    } else {
      if (loading) {
        return (
          <div
            className={classNames(
              'w-full h-[63px] mb-2.5 rounded-md bg-gray-500/40 animate-skeleton',
              'bg-gradient-to-r from-gray-500/40 via-[20%] via-gray-500/10 to-[40%] to-gray-500/40 bg-[length:1000px]',
            )}
          />
        );
      }
      if (noImprovement) {
        return (
          <div className="w-full min-h-[63px] border border-gray-400 rounded-md p-3 mb-2.5">
            <div className="flex items-center text-white font-semibold">
              <TickIcon className="w-4 h-4 fill-current text-green-500 mr-3" />
              All good!
            </div>
            <div className="text-gray-200">Your check is already clear, no improvement necessary!</div>
          </div>
        );
      }
      if (cannotImprove) {
        return (
          <div className="w-full min-h-[63px] border border-gray-400 rounded-md p-3 mb-2.5">
            <div className="flex items-center text-white font-semibold">
              <WarningTriangleIcon className="w-4 h-4 fill-current text-[#FFB202] mr-3" />
              This check cannot be improved.
            </div>
            <div className="text-gray-200">{result.reason}</div>
          </div>
        );
      }

      if (improved) {
        return (
          <div>
            <div
              style={{ '--selectedvoice': '122 76 252' }}
              className="w-full mb-2.5 p-2.5 rounded-md border bg-selectedvoice/10 text-[#B89EFF] border-selectedvoice/50 whitespace-pre-wrap"
            >
              {improvedPrompt}
            </div>
            <div className="flex items-center space-x-2.5 justify-end">
              <Button
                variant={BUTTON_TYPES.SECONDARY}
                onClick={() => {
                  setClicked(false);
                }}
              >
                Reject
              </Button>
              <Button
                variant={BUTTON_TYPES.SECONDARY}
                onClick={() => {
                  setClicked(false);
                  setFieldValue('definition', improvedPrompt);
                }}
              >
                Accept
              </Button>
            </div>
          </div>
        );
      }
    }
  }

  return (
    <div className="mb-6">
      <div className="w-full flex justify-end mb-2.5">
        <Button
          disabled={!values.condition || !values.definition || (clicked && !data?.ready)}
          variant={BUTTON_TYPES.SECONDARY}
          onClick={() => {
            let needsRefetch = true;
            if (condition !== values.condition) {
              setCondition(values.condition);
              needsRefetch = false;
            }
            if (definition !== values.definition) {
              setDefinition(values.definition);
              needsRefetch = false;
            }
            if (!clicked) {
              setClicked(true);
              if (!data) {
                needsRefetch = false;
              }
            }
            if (needsRefetch) {
              refetch();
            }
          }}
        >
          Improve my check
        </Button>
      </div>
      {clicked ? renderResult() : null}
    </div>
  );
}

function TestCheck({ checkCondition, checkDefinition, checkExample, checkTestContent }) {
  const [clicked, setClicked] = useState(false);
  const [condition, setCondition] = useState(checkCondition);
  const [definition, setDefinition] = useState(checkDefinition);
  const [example, setExample] = useState(checkExample);
  const [text, setText] = useState(checkTestContent);

  const params = useMemo(() => {
    return {
      checks: JSON.stringify(
        [
          {
            name: 'test check',
            condition,
            definition,
            example,
          },
        ].map(formatChecksForAI),
      ),
      elements: JSON.stringify([
        {
          selector: '#test',
          text,
          type: 'other',
        },
      ]),
    };
  }, [condition, definition, example, text]);

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
    }),
    [],
  );

  const {
    data,
    error: aiError,
    refetch,
  } = useCrazyAI({
    type: AI_PROMPTS.CHECKS,
    params,
    context,
    enabled: clicked,
  });

  const result = useMemo(() => {
    if (!data?.ready) return null;
    let result;
    try {
      result = JSON.parse(data.text);
    } catch {
      return null;
    }
    return result.failures;
  }, [data]);

  function renderResult() {
    const loading = !data?.ready;
    const failed = data?.ready && result?.[0]?.status === 'fail';
    const passed = data?.ready && result?.length === 0;
    const error = aiError || (data?.ready && result == null);

    if (error) {
      return (
        <div className="w-full h-[63px] border border-gray-400 rounded-md p-3 mb-2.5 text-gray-200">
          There was an error while testing your example. Please try again.
        </div>
      );
    } else {
      if (loading) {
        return (
          <div
            className={classNames(
              'w-full h-[63px] mb-2.5 rounded-md bg-gray-500/40 animate-skeleton',
              'bg-gradient-to-r from-gray-500/40 via-[20%] via-gray-500/10 to-[40%] to-gray-500/40 bg-[length:1000px]',
            )}
          />
        );
      }
      if (passed) {
        return (
          <div className="w-full min-h-[63px] border border-gray-400 rounded-md p-3 mb-2.5">
            <div className="flex items-center text-white font-semibold">
              <div className="relative mr-2.5">
                <BadgeTickIcon className="w-[16px] h-[16px] fill-current text-green-500 z-10 relative" />
                <div className="absolute rounded-full bg-white w-3 h-3 top-0.5 left-0.5 z-0" />
              </div>
              Passed check
            </div>
            <div className="text-gray-200">This example complies with your check.</div>
          </div>
        );
      }
      if (failed) {
        return (
          <div className="w-full min-h-[63px] border border-gray-400 rounded-md p-3 mb-2.5">
            <div className="flex items-center text-white font-semibold">
              <div className="relative mr-2.5">
                <BadgeCrossIcon className="w-[16px] h-[16px] fill-current text-[#FF993B] z-10 relative" />
                <div className="absolute rounded-full bg-white w-3 h-3 top-0.5 left-0.5 z-0" />
              </div>
              Failed check
            </div>
            <div className="text-gray-200">{result[0].reason}</div>
          </div>
        );
      }
    }
  }

  return (
    <div className="mb-6">
      {clicked ? renderResult() : null}
      <div className="w-full flex justify-end">
        <Button
          disabled={!checkDefinition || !checkTestContent}
          variant={BUTTON_TYPES.SECONDARY}
          onClick={() => {
            let needsRefetch = true;
            if (condition !== checkCondition) {
              setCondition(checkCondition);
              needsRefetch = false;
            }
            if (definition !== checkDefinition) {
              setDefinition(checkDefinition);
              needsRefetch = false;
            }
            if (example !== checkExample) {
              setExample(checkExample);
              needsRefetch = false;
            }
            if (text !== checkTestContent) {
              setText(checkTestContent);
              needsRefetch = false;
            }
            if (!clicked) {
              setClicked(true);
              needsRefetch = false;
            }
            if (needsRefetch) {
              refetch();
            }
          }}
        >
          Test check
        </Button>
      </div>
    </div>
  );
}

function Checks({ actions, editCheckClicked, checkEditorState, closeEditor, containerRef, setMetadata }) {
  const [showAll, setShowAll] = useState(false);
  const { values } = useFormikContext();
  const checks = showAll ? values.checks : values.checks.slice(0, 5);

  return (
    <>
      {checks.map((check, index) => (
        <Fragment key={check.name}>
          {checkEditorState.open && checkEditorState.index === index ? (
            <CheckEditor
              close={closeEditor}
              checkEditorState={checkEditorState}
              containerRef={containerRef}
              replace={actions.replace}
              index={index}
              setMetadata={setMetadata}
            />
          ) : (
            <CheckItem index={index} actions={actions} editCheckClicked={editCheckClicked} setMetadata={setMetadata} />
          )}
        </Fragment>
      ))}
      {values.checks.length > 5 && (
        <>
          <div className="w-full flex justify-center">
            <button
              type="button"
              className="font-semibold text-white"
              onClick={() => {
                setShowAll(!showAll);
              }}
            >
              {showAll ? 'Show less' : 'Show more'}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function CheckItem({ actions, index, editCheckClicked, setMetadata }) {
  const { values, handleChange } = useFormikContext();
  const check = values.checks[index];

  const { useSelector } = useMainMachine();
  const userEmail = useSelector(({ context }) => context.userEmail);

  const canEdit = check.default !== true;
  const canDuplicate = values.checks.length < MAX_CHECKS;
  const canDelete = check.default !== true;

  const menuEnabled = canEdit || canDuplicate || canDelete;

  return (
    <div
      className="w-full px-3.75 py-2.5 border border-gray-400 mb-2.5 rounded-md flex items-start bg-gray-700 relative"
      data-testid="CheckItem"
    >
      <div className="w-6">
        <Checkbox
          checked={check.enabled}
          onChange={(e) => {
            handleChange(e);
            setMetadata((x) => ({
              ...x,
              checks_enabled_disabled: [
                ...x.checks_enabled_disabled,
                { name: check.name, action: check.enabled ? 'disabled' : 'enabled' },
              ],
            }));
          }}
          name={`checks[${index}].enabled`}
          aria-label={check.enabled ? 'disable check' : 'enable check'}
        />
      </div>
      <div className="ml-2.5">
        <div className="flex items-center">
          <span
            className={classNames('shrink-0 mr-1.25', {
              'text-gray-200': check.enabled,
              'text-white/30': !check.enabled,
            })}
          >
            {check.name}
          </span>
          {check.updatedBy ? (
            <span
              className={classNames('truncate', {
                'text-gray-300': check.enabled,
                'text-white/30': !check.enabled,
              })}
            >
              &nbsp;by {check.updatedBy}
            </span>
          ) : null}
        </div>
        <span
          className={classNames('truncate', {
            'text-gray-300': check.enabled,
            'text-white/30': !check.enabled,
          })}
        >
          {check.note}
        </span>
      </div>
      <Popover className="absolute right-3">
        <Popover.Button
          className={classNames('p-1', {
            'text-gray-200 hover:text-white': menuEnabled,
            'text-white/30 cursor-not-allowed': !menuEnabled,
          })}
          disabled={!menuEnabled}
        >
          <MenuIcon className="w-3 h-3 fill-current" />
        </Popover.Button>
        <Transition
          appear={true}
          as={Fragment}
          leave="transition-opacity ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          enter="transition-opacity ease-in duration-100"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <Popover.Panel className="absolute z-10 right-0 top-full bg-gray-500 rounded-md p-1.25 shadow-md flex flex-col w-[120px]">
            {({ close }) => (
              <div className="text-gray-200 font-semibold">
                {canEdit ? (
                  <MenuItem
                    title="Edit"
                    Icon={EditIcon}
                    onClick={() => {
                      editCheckClicked(index);
                      close();
                    }}
                  />
                ) : null}
                {canDuplicate ? (
                  <MenuItem
                    title="Duplicate"
                    Icon={DuplicateIcon}
                    onClick={() => {
                      const newName = appendCopy(
                        check.name,
                        values.checks.map((v) => v.name),
                        CHECK_NAME_LIMIT,
                      );
                      actions.unshift({
                        ...check,
                        id: uid.rnd(),
                        name: newName,
                        createdBy: userEmail,
                        updatedBy: userEmail,
                        default: false,
                        enabled: true,
                      });
                      setMetadata((x) => ({
                        ...x,
                        checks_edited: [...x.checks_edited, { name: newName, action: 'added' }],
                      }));
                      editCheckClicked(0);
                      close();
                    }}
                  />
                ) : null}
                {canDelete ? (
                  <MenuItem
                    title="Delete"
                    Icon={DeleteIcon}
                    onClick={() => {
                      actions.remove(index);
                      setMetadata((x) => ({
                        ...x,
                        checks_edited: [...x.checks_edited, { name: check.name, action: 'deleted' }],
                      }));
                      close();
                    }}
                  />
                ) : null}
              </div>
            )}
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
}
